/*
 * lab-lot.js — DIRECTION.md §6 interaction 11 and §5.12 (group E).
 *
 * The kept <lot-trace> (assets/bottle-trace.js) resolves ?lot= against the server-rendered
 * records and reveals the match. This file adds what the label's lot field needs on top:
 *
 *  - every lot input (input.field__input[autocapitalize="characters"]) uppercases as typed,
 *    with the caret preserved — the lot page, the 404 and the accordion rows all render the
 *    same lab-lot snippet, so one delegated listener covers them;
 *  - <lab-lot-register> answers a lookup in place. A submitted code becomes ?lot= in the address
 *    (pushState; back and forward replay it), and one step changes everything that depends on
 *    it: the record (or the honest miss, with the code prefilled in a contact link), the oil to
 *    reorder, the live status line, and the land map — told through `lab:lot-field` { name, code,
 *    animate } and <html data-lot-field>, for a map that connects later. The record never waits
 *    for the map. A miss or an emptied search sends { name: null }, and the map lets go.
 *    Without JavaScript the form is a plain GET to the same page and the record prints on arrival.
 *    Nothing is read out of a code: it is matched to a record, loosely (case, spaces, dashes, a
 *    missing L, O for 0 — the same rule as LotTrace.norm in assets/bottle-trace.js).
 *
 *  - the lot card (data-card, only for those allowed to print): a matched record stamps the
 *    printable A6 card from its server-rendered half, the panel takes the packer's ship date and
 *    prints on the button (connectCard … clearPrint below).
 *
 * State is read in connectedCallback; listeners go with the element (AbortController).
 */
(() => {
  const LOT_INPUT = 'input.field__input[autocapitalize="characters"]';

  const uppercaseKeepingCaret = (input) => {
    const { value, selectionStart, selectionEnd } = input;
    const upper = value.toUpperCase();
    if (upper === value) return;
    input.value = upper;
    if (typeof selectionStart === 'number' && typeof selectionEnd === 'number') {
      try {
        input.setSelectionRange(selectionStart, selectionEnd);
      } catch (error) {
        /* some input types refuse selection ranges: the value is still uppercased */
      }
    }
  };

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches(LOT_INPUT)) return;
    uppercaseKeepingCaret(input);
  });

  const readParam = (name) => {
    try {
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (error) {
      return '';
    }
  };

  // same loose match as LotTrace.norm in assets/bottle-trace.js: case, punctuation, a missing L, O for 0
  const norm = (value) => String(value == null ? '' : value).toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/O/g, '0').replace(/^L/, '');

  // a token as a canvas can paint it: the wide-gamut tokens are oklch(), which an older canvas
  // ignores; the 2D context serializes what it accepts and keeps the fallback otherwise
  const canvasColour = (value, fallback) => {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return fallback;
    context.fillStyle = fallback;
    context.fillStyle = String(value || '').trim() || fallback;
    return context.fillStyle;
  };

  if (!customElements.get('lab-lot-register')) {
    class LabLotRegister extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        const { signal } = this.abort;
        this.param = this.dataset.param || 'lot';
        this.input = this.querySelector(LOT_INPUT);
        this.miss = this.querySelector('[data-lot-miss]');
        this.next = this.querySelector('[data-lot-next]');
        this.found = this.querySelector('[data-lot-found]');
        this.contactUrl = this.dataset.contactUrl || '';

        // a label's QR names the oil in hand (?olio=settembre|blend): show that intro only
        this.oil = readParam('olio');
        this.querySelectorAll('[data-olio]').forEach((node) => {
          node.hidden = node.dataset.olio !== this.oil;
        });
        // the lookup is a GET form: carry the oil through it, or the intro drops on submit
        const form = this.input && this.input.form;
        if (this.oil && form && !form.elements.namedItem('olio')) {
          const keep = document.createElement('input');
          keep.type = 'hidden';
          keep.name = 'olio';
          keep.value = this.oil;
          form.append(keep);
        }

        // the full register answers in place (the compact field keeps posting to this page)
        if (form && form.matches('[data-lot-form]')) {
          form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
          window.addEventListener('popstate', () => this.resolve(readParam(this.param), { animate: false, scroll: false, sync: true }), { signal });
        }

        const requested = readParam(this.param);
        if (this.input && requested) this.input.value = requested.toUpperCase();

        // the lot card: only rendered for those allowed to print (sections/lab-lot-register.liquid)
        if ('card' in this.dataset) this.connectCard(signal);
        // the editor previews a lot with no lookup (data-card-preview is only set in design mode)
        const initial = requested || this.dataset.cardPreview || '';

        // paint after lot-trace has resolved the records (see the header note)
        this.frame = window.requestAnimationFrame(() => this.resolve(initial, { animate: false, scroll: Boolean(requested) }));

        // a merchant selecting a record block in the editor hides the miss line
        this.addEventListener('shopify:block:select', () => this.hideMiss(), { signal });
      }

      disconnectedCallback() {
        if (this.abort) this.abort.abort();
        if (this.frame) window.cancelAnimationFrame(this.frame);
      }

      records() {
        return Array.from(this.querySelectorAll('[data-lot-code]'));
      }

      onSubmit(event) {
        const typed = (this.input ? this.input.value : '').trim().toUpperCase();
        if (!typed) return; // `required` has already asked for the code
        event.preventDefault();
        // a code that matches a record is written back as the record writes it ("o20 10-25" is
        // O201025), in the field and in the address; an unknown one stays as typed
        const record = this.records().find((r) => norm(r.dataset.lotCode) === norm(typed));
        const code = record ? record.dataset.lotCode : typed;
        if (this.input) this.input.value = code;
        try {
          const url = new URL(window.location.href);
          const same = norm(url.searchParams.get(this.param)) === norm(code);
          url.searchParams.set(this.param, code);
          window.history[same ? 'replaceState' : 'pushState']({ lot: code }, '', url);
        } catch (error) {
          /* history unavailable (a sandboxed frame): the answer still prints */
        }
        this.resolve(code, { animate: true, scroll: true });
      }

      // One step: record, miss, oil, status and map all follow the same answer.
      resolve(requested, { animate = false, scroll = false, sync = false } = {}) {
        const code = String(requested || '').trim().toUpperCase();
        const wanted = norm(code);
        // back and forward bring the address's code into the field too, focused or not
        if (this.input && (sync || document.activeElement !== this.input)) this.input.value = code;
        const matched = wanted ? this.records().find((record) => norm(record.dataset.lotCode) === wanted) || null : null;

        this.records().forEach((record) => { record.hidden = record !== matched; });
        if (matched || !wanted) this.hideMiss();
        else this.showMiss(code);
        this.showNext(Boolean(matched));
        if (this.found) {
          this.found.textContent = matched ? (this.found.dataset.template || '').replace('[lot]', matched.dataset.lotCode) : '';
        }

        // the map: the found lot's field, or nothing
        const root = document.documentElement;
        const field = matched ? matched.dataset.campo || '' : '';
        if (field) {
          root.dataset.lotField = field;
          root.dataset.lotCode = matched.dataset.lotCode;
        } else {
          delete root.dataset.lotField;
          delete root.dataset.lotCode;
        }
        document.dispatchEvent(new CustomEvent('lab:lot-field', {
          detail: { name: field || null, code: matched ? matched.dataset.lotCode : '', animate },
        }));

        // the answer prints under the form, below the fold on a phone: bring it up, or a reader
        // who just pressed Verifica sees the same screen and thinks nothing happened
        const answer = matched || (wanted ? this.miss : null);
        if (scroll && answer && !answer.hidden) {
          const box = answer.getBoundingClientRect();
          if (box.top < 0 || box.top > window.innerHeight * 0.6) answer.scrollIntoView({ block: 'center' });
        }

        if (this.cardShell) this.stampCard(matched);
      }

      /*
       * The lot card ("La cartolina del lotto", snippets/lab-lot-card.liquid). The server renders
       * everything: the shell once (<template data-lot-card>) and each lot's half in its record
       * (<template data-lot-card-fields>). This only clones and moves nodes, writes the ship date
       * the packer types, draws the QR and prints on the button. It builds no URL, reads no fact,
       * runs no timer and never prints on its own.
       */
      connectCard(signal) {
        this.cardShell = this.querySelector('template[data-lot-card]');
        this.cardPanel = this.querySelector('[data-lot-card-panel]');
        this.cardSlot = this.querySelector('[data-lot-card-slot]');
        this.cardShip = this.querySelector('[data-card-ship]');
        this.card = null;
        this.qrReady = null;
        if (!this.cardShell || !this.cardPanel || !this.cardSlot) {
          this.cardShell = null;
          return;
        }
        if (this.cardShip) {
          this.cardShip.addEventListener('input', () => this.writeShipDate(), { signal });
        }
        // the panel's form has no action: Enter in the date field prints nothing and posts nothing
        this.cardPanel.addEventListener('submit', (event) => event.preventDefault(), { signal });
        const print = this.cardPanel.querySelector('[data-card-print]');
        if (print) print.addEventListener('click', () => this.printCard(), { signal });
        window.addEventListener('afterprint', () => this.clearPrint(), { signal });
      }

      // yyyy-mm-dd → dd/mm/yyyy by string, never through Date (no UTC shift of the day)
      shipValue() {
        const raw = this.cardShip ? this.cardShip.value : '';
        const parts = raw.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : '';
      }

      // an instant swap, a date is data: empty, the field's hairline is the line to write on
      writeShipDate() {
        if (!this.card) return;
        const value = this.shipValue();
        this.card.querySelectorAll('[data-card-ship-value]').forEach((node) => { node.textContent = value; });
      }

      stampCard(record) {
        this.cardSlot.replaceChildren();
        const fragment = record ? record.querySelector('template[data-lot-card-fields]') : null;
        if (!fragment) {
          this.cardPanel.hidden = true;
          this.cardSlot.hidden = true;
          this.card = null;
          this.qrReady = null;
          return;
        }

        // importNode, not cloneNode: a clone of template content stays in the template's inert
        // document, where the QR image never loads while the card is kept off the page
        const card = document.importNode(this.cardShell.content.firstElementChild, true);
        const half = document.importNode(fragment.content, true);
        const band = half.querySelector('.lc__band');
        const fields = half.querySelector('[data-fields]');
        const link = half.querySelector('[data-link]');
        const bandSlot = card.querySelector('[data-slot="band"]');
        const dataSlot = card.querySelector('[data-slot="data"]');
        const linkSlot = card.querySelector('[data-slot="link"]');
        // the colourway is the lot's: the band carries it and the stripes below take the same
        const scheme = band ? Array.from(band.classList).find((name) => name.startsWith('scheme-')) : '';
        if (scheme) card.classList.add(scheme);
        if (band && bandSlot) bandSlot.replaceWith(band);
        if (fields && dataSlot) dataSlot.replaceChildren(...fields.children);
        if (link && linkSlot) linkSlot.textContent = link.textContent;
        this.card = card;
        this.writeShipDate();

        // interaction 11 continued: the panel, then the zones in the order the label is read
        const start = Number.parseInt(fragment.dataset.iStart || '0', 10) || 0;
        this.cardPanel.classList.add('print-row');
        this.cardPanel.style.setProperty('--i', String(start));
        Array.from(card.children).forEach((zone, index) => {
          zone.classList.add('print-row');
          zone.style.setProperty('--i', String(start + 1 + index));
        });
        // the card is a new node each time, so a new lookup prints it again; the panel is the
        // same node, so its print restarts with it (no animation under reduced motion or the editor)
        this.cardPanel.hidden = false;
        this.cardPanel.getAnimations().forEach((animation) => { animation.cancel(); animation.play(); });

        if ('cardShow' in this.dataset) {
          this.cardSlot.append(card);
          this.cardSlot.hidden = false;
        } else {
          this.cardSlot.hidden = true;
        }
        this.drawQr(card, fields ? fields.dataset.qrUrl || '' : '');
      }

      drawQr(card, url) {
        this.qrReady = null;
        const holder = card.querySelector('[data-lot-qr]');
        if (!holder || holder.childElementCount > 0 || !url) return;
        if (typeof window.QRCode !== 'function') {
          // the library did not load: the holder stays empty (CSS hides it), the printed link remains
          if (!this.qrWarned) console.warn('lab-lot: vendor/qrcode.js is not available; the lot card prints without its QR.');
          this.qrWarned = true;
          return;
        }
        const root = getComputedStyle(document.documentElement);
        new window.QRCode(holder, {
          text: url,
          width: 256,
          height: 256,
          colorDark: canvasColour(root.getPropertyValue('--ink'), '#000000'),
          colorLight: canvasColour(root.getPropertyValue('--cream'), '#ffffff'),
          correctLevel: window.QRCode.CorrectLevel.M,
        });
        // keep only an <img> of our own with the data URL, so every clone for paper carries the
        // code. Not the library's <img>: it sets that one's src again later, asynchronously,
        // which would reject the decode below.
        const canvas = holder.querySelector('canvas');
        if (!canvas) return; // the library's table fallback clones as it is
        const img = document.createElement('img');
        img.alt = '';
        img.src = canvas.toDataURL('image/png');
        holder.replaceChildren(img);
        // load, not decode(): a detached image's decode() can stay pending (the card is kept
        // off the page when the preview is off), and the print would wait for ever
        this.qrReady = img.complete ? null : new Promise((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => {
            console.warn('lab-lot: the lot card QR image did not load; the card prints without it.');
            resolve();
          }, { once: true });
        });
      }

      async printCard() {
        if (!this.card) return;
        this.clearPrint();
        if (this.qrReady) await this.qrReady;
        const sheet = this.dataset.cardSheet === 'a4x4' ? 'a4x4' : 'a6';
        const paper = document.createElement('div');
        paper.className = sheet === 'a4x4' ? 'lc-print lc-print--a4x4' : 'lc-print';
        // hidden on screen even when the card's stylesheet is print-only; @media print overrides it
        paper.style.display = 'none';
        const copies = sheet === 'a4x4' ? 4 : 1;
        for (let n = 0; n < copies; n += 1) {
          const copy = this.card.cloneNode(true);
          copy.removeAttribute('aria-hidden');
          [copy, ...copy.querySelectorAll('.print-row')].forEach((node) => {
            node.classList.remove('print-row');
            node.style.removeProperty('--i');
            if (!node.getAttribute('style')) node.removeAttribute('style');
          });
          paper.append(copy);
        }
        document.body.append(paper);
        document.documentElement.dataset.print = 'lot-card';
        window.print();
      }

      clearPrint() {
        document.querySelectorAll('body > .lc-print').forEach((node) => node.remove());
        if (document.documentElement.dataset.print === 'lot-card') delete document.documentElement.dataset.print;
      }

      // the oil to reorder: the one the label's QR named, or both when it named none
      showNext(visible) {
        if (!this.next) return;
        this.next.hidden = !visible;
        const rows = Array.from(this.next.querySelectorAll('[data-olio-next]'));
        const named = rows.some((row) => row.dataset.olioNext === this.oil);
        rows.forEach((row) => { row.hidden = named && row.dataset.olioNext !== this.oil; });
      }

      contactHref(code) {
        if (!this.contactUrl) return '';
        try {
          const url = new URL(this.contactUrl, window.location.origin);
          url.searchParams.set('reason', 'ordine');
          url.searchParams.set('lot', code);
          url.hash = this.dataset.contactHash || 'scrivici';
          return url.toString();
        } catch (error) {
          return this.contactUrl;
        }
      }

      showMiss(code) {
        if (!this.miss) return;
        // The miss line is also lot-trace's [data-lot-notice]: when records exist lot-trace
        // has already written its plain text here, so the children are rebuilt every time.
        const codeNode = document.createElement('span');
        codeNode.className = 'mono';
        codeNode.textContent = code;
        // The CSS gap separates the code from the link visually, but a gap is not text:
        // without a separator the role="status" region announces "ABCNon troviamo…".
        // .visually-hidden is position: absolute, so it is not a flex item here and adds
        // no second gap, while still contributing to the live region's text.
        const separator = document.createElement('span');
        separator.className = 'visually-hidden';
        separator.textContent = ': ';
        const text = this.miss.dataset.template || '';
        const href = this.contactHref(code);
        let action;
        if (href) {
          action = document.createElement('a');
          action.className = 'btn-text';
          action.href = href;
          action.textContent = text;
        } else {
          action = document.createElement('span');
          action.textContent = text;
        }
        // un-hidden first so the role="status" region is in the tree when its text lands
        this.miss.hidden = false;
        this.miss.replaceChildren(codeNode, separator, action);
      }

      hideMiss() {
        if (this.miss) this.miss.hidden = true;
      }
    }
    customElements.define('lab-lot-register', LabLotRegister);
  }
})();
